import React, { useState, useRef, useEffect } from 'react';
import './CustomDatePicker.css';

function CustomDatePicker({
  value,
  onChange,
  placeholder = "Select date",
  maxDate = new Date(),
  minDate = null,
  label = "",
  icon = true
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(value ? new Date(value) : new Date());
  const [viewMode, setViewMode] = useState('days'); // 'days', 'months', 'years'
  const [dropdownStyle, setDropdownStyle] = useState({});
  const datePickerRef = useRef(null);
  const inputRef = useRef(null);

  const today = new Date();
  const maxDateObj = maxDate ? new Date(maxDate) : today;
  const minDateObj = minDate ? new Date(minDate) : null;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
        setViewMode('days');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const updatePosition = () => {
        const inputRect = inputRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const windowWidth = window.innerWidth;
        const dropdownWidth = 280;
        const dropdownHeight = 400;

        let top = inputRect.bottom + 8;
        let left = inputRect.left;

        // Adjust if dropdown would go off the right edge
        if (left + dropdownWidth > windowWidth) {
          left = windowWidth - dropdownWidth - 16;
        }

        // Adjust if dropdown would go off the bottom edge
        if (top + dropdownHeight > windowHeight) {
          top = inputRect.top - dropdownHeight - 8;
        }

        // Ensure it doesn't go off the top or left edges
        if (top < 8) {
          top = 8;
        }
        if (left < 8) {
          left = 8;
        }

        setDropdownStyle({
          top: `${top}px`,
          left: `${left}px`,
        });
      };

      updatePosition();

      // Update position on scroll or resize
      const handleScroll = () => updatePosition();
      const handleResize = () => updatePosition();

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isOpen]);

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const handleDateSelect = (date) => {
    if (isDateDisabled(date)) return;
    onChange(date.toISOString().split('T')[0]);
    setIsOpen(false);
    setViewMode('days');
  };

  const handleMonthSelect = (monthIndex) => {
    const newDate = new Date(currentDate.getFullYear(), monthIndex, 1);
    setCurrentDate(newDate);
    setViewMode('days');
  };

  const handleYearSelect = (year) => {
    const newDate = new Date(year, currentDate.getMonth(), 1);
    setCurrentDate(newDate);
    setViewMode('months');
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    if (date > today) return true; // Disable future dates
    if (maxDateObj && date > maxDateObj) return true;
    if (minDateObj) {
      const minDate = new Date(minDateObj);
      minDate.setHours(0, 0, 0, 0); // Start of min date
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0); // Start of check date
      if (checkDate < minDate) return true; // Disable dates before minDate
    }
    return false;
  };

  const isSelectedDate = (date) => {
    if (!value || !date) return false;
    const selectedDate = new Date(value);
    return date.toDateString() === selectedDate.toDateString();
  };

  const isToday = (date) => {
    if (!date) return false;
    return date.toDateString() === today.toDateString();
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateYear = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(currentDate.getFullYear() + direction);
    setCurrentDate(newDate);
  };

  const getYearsRange = () => {
    const currentYear = currentDate.getFullYear();
    const today = new Date();
    const currentRealYear = today.getFullYear();
    const startYear = Math.floor(currentYear / 12) * 12;
    const years = [];
    for (let i = startYear; i < startYear + 12; i++) {
      if (i <= currentRealYear) { // Only show years up to current year
        years.push(i);
      }
    }
    return years;
  };

  const renderDaysView = () => {
    const days = getDaysInMonth(currentDate);

    return (
      <div className="custom-calendar">
        <div className="calendar-header">
          <button
            className="nav-button"
            onClick={() => navigateMonth(-1)}
            type="button"
          >
            ‹
          </button>

          <div className="header-buttons">
            <button
              className="month-year-button"
              onClick={() => setViewMode('months')}
              type="button"
            >
              {months[currentDate.getMonth()]}
            </button>
            <button
              className="month-year-button"
              onClick={() => setViewMode('years')}
              type="button"
            >
              {currentDate.getFullYear()}
            </button>
          </div>

          <button
            className="nav-button"
            onClick={() => navigateMonth(1)}
            type="button"
          >
            ›
          </button>
        </div>

        <div className="calendar-weekdays">
          {weekdays.map(day => (
            <div key={day} className="weekday">{day}</div>
          ))}
        </div>

        <div className="calendar-days">
          {days.map((date, index) => (
            <button
              key={index}
              className={`calendar-day ${!date ? 'empty' : ''} ${isSelectedDate(date) ? 'selected' : ''} ${isToday(date) ? 'today' : ''} ${isDateDisabled(date) ? 'disabled' : ''}`}
              onClick={() => date && handleDateSelect(date)}
              disabled={!date || isDateDisabled(date)}
              type="button"
            >
              {date?.getDate()}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderMonthsView = () => {
    const today = new Date();
    const currentRealYear = today.getFullYear();
    const currentRealMonth = today.getMonth();
    const selectedYear = currentDate.getFullYear();

    // Check if we can navigate forward in years
    const canNavigateYearForward = selectedYear < currentRealYear;

    return (
      <div className="custom-calendar">
        <div className="calendar-header">
          <button
            className="nav-button"
            onClick={() => navigateYear(-1)}
            type="button"
          >
            ‹
          </button>

          <button
            className="month-year-button"
            onClick={() => setViewMode('years')}
            type="button"
          >
            {currentDate.getFullYear()}
          </button>

          <button
            className="nav-button"
            onClick={() => navigateYear(1)}
            disabled={!canNavigateYearForward}
            type="button"
            style={{ opacity: canNavigateYearForward ? 1 : 0.3, cursor: canNavigateYearForward ? 'pointer' : 'not-allowed' }}
          >
            ›
          </button>
        </div>

        <div className="calendar-months">
          {months.map((month, index) => {
            let isMonthDisabled = false;

            // Check if month should be disabled due to future date restriction
            if (selectedYear === currentRealYear && index > currentRealMonth) {
              isMonthDisabled = true;
            }

            // Check if month should be disabled due to minDate restriction
            if (minDateObj) {
              const minDate = new Date(minDateObj);
              const monthDate = new Date(selectedYear, index, 1);
              const minMonthDate = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
              if (monthDate < minMonthDate) {
                isMonthDisabled = true;
              }
            }

            return (
              <button
                key={month}
                className={`calendar-month ${currentDate.getMonth() === index ? 'selected' : ''} ${isMonthDisabled ? 'disabled' : ''}`}
                onClick={() => !isMonthDisabled && handleMonthSelect(index)}
                disabled={isMonthDisabled}
                type="button"
                style={{
                  opacity: isMonthDisabled ? 0.3 : 1,
                  cursor: isMonthDisabled ? 'not-allowed' : 'pointer'
                }}
              >
                {month}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderYearsView = () => {
    const years = getYearsRange();
    const today = new Date();
    const currentRealYear = today.getFullYear();
    const minYear = minDateObj ? minDateObj.getFullYear() : null;

    // Check if we can navigate forward (would show future years)
    const canNavigateForward = Math.floor((currentDate.getFullYear() + 12) / 12) * 12 <= currentRealYear;

    return (
      <div className="custom-calendar">
        <div className="calendar-header">
          <button
            className="nav-button"
            onClick={() => {
              const newDate = new Date(currentDate);
              newDate.setFullYear(currentDate.getFullYear() - 12);
              setCurrentDate(newDate);
            }}
            type="button"
          >
            ‹
          </button>

          <div className="month-year-button">
            {years.length > 0 ? `${years[0]} - ${years[years.length - 1]}` : currentRealYear}
          </div>

          <button
            className="nav-button"
            onClick={() => {
              const newDate = new Date(currentDate);
              newDate.setFullYear(currentDate.getFullYear() + 12);
              setCurrentDate(newDate);
            }}
            disabled={!canNavigateForward}
            type="button"
            style={{ opacity: canNavigateForward ? 1 : 0.3, cursor: canNavigateForward ? 'pointer' : 'not-allowed' }}
          >
            ›
          </button>
        </div>

        <div className="calendar-years">
          {years.map((year) => {
            const isYearDisabled = minYear && year < minYear;

            return (
              <button
                key={year}
                className={`calendar-year ${currentDate.getFullYear() === year ? 'selected' : ''} ${isYearDisabled ? 'disabled' : ''}`}
                onClick={() => !isYearDisabled && handleYearSelect(year)}
                disabled={isYearDisabled}
                type="button"
                style={{
                  opacity: isYearDisabled ? 0.3 : 1,
                  cursor: isYearDisabled ? 'not-allowed' : 'pointer'
                }}
              >
                {year}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="custom-date-picker" ref={datePickerRef}>
      {label && (
        <label className="custom-date-label">
          {icon && (
            <svg className="date-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          )}
          {label}
        </label>
      )}

      <div className="custom-date-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          value={value ? formatDate(value) : ''}
          placeholder={placeholder}
          onClick={() => setIsOpen(!isOpen)}
          readOnly
          className="custom-date-input"
        />
        <button
          type="button"
          className="custom-date-trigger"
          onClick={() => setIsOpen(!isOpen)}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
        </button>
      </div>

      {isOpen && (
        <div className="custom-date-dropdown" style={dropdownStyle}>
          {viewMode === 'days' && renderDaysView()}
          {viewMode === 'months' && renderMonthsView()}
          {viewMode === 'years' && renderYearsView()}

          <div className="calendar-actions">
            <button
              type="button"
              className="calendar-action-btn clear-btn"
              onClick={() => {
                onChange('');
                setIsOpen(false);
                setViewMode('days');
              }}
            >
              Clear
            </button>
            <button
              type="button"
              className="calendar-action-btn today-btn"
              onClick={() => handleDateSelect(today)}
              disabled={isDateDisabled(today)}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomDatePicker;